import React from 'react'
import { Link } from 'react-router-dom'
import { Tooltip } from 'antd'
import { StockTakeInputNumber } from 'src/utils/stockTakeStepperAria'
import styled from 'styled-components/macro'
import {
  WASTAGE_COPY,
  stockWastagePeriodHref,
  stockWastageRecordHref,
  wastageBackTarget,
  wastageLinkTo,
} from 'src/constants/wastage'

/**
 * A main-take line owned by Waste shows the live sum and does not accept a typed count.
 * Own live waste of 0 releases the line, including after the last entry is removed.
 * A partial count stays editable. A locked take is already view-only.
 */
export const stockWastageIsFromTracker = (
  product,
  { isPartial = false } = {},
) => {
  if (isPartial || !product?.liveWastageManaged) return false
  const wastage = Number(product?.wastage)
  return Number.isFinite(wastage) && wastage > 0
}

/** Unique dish titles, in the order they were stored. */
export const stockWastageDishLabel = dishes => {
  const names = []
  const seen = new Set()
  ;(Array.isArray(dishes) ? dishes : []).forEach(dish => {
    const name = String(dish || '').trim()
    const key = name.toLowerCase()
    if (!name || seen.has(key)) return
    seen.add(key)
    names.push(name)
  })
  return names.join(', ')
}

export const StockWastageQuantity = ({
  productId,
  productTitle,
  value,
  fromTracker = false,
  includesVat = false,
  dishes = [],
  locked = false,
  onChange,
}) => {
  const readOnly = locked || fromTracker
  const fromWasteLabel =
    fromTracker && includesVat
      ? WASTAGE_COPY.fromWasteIncludingVat
      : WASTAGE_COPY.fromWaste
  const dishLabel = fromTracker ? stockWastageDishLabel(dishes) : ''
  return (
    <Cell
      data-testid={productId ? `stock-wastage-${productId}` : 'stock-wastage'}
      data-managed={fromTracker ? 'true' : 'false'}
    >
      <Tooltip
        title={
          fromTracker
            ? WASTAGE_COPY.fromWasteHint
            : 'Quantity wasted or lost during this stock period'
        }
      >
        <StockTakeInputNumber
          field='wastage'
          productTitle={productTitle}
          value={value}
          onChange={displayQty => {
            if (fromTracker || !onChange) return
            onChange(displayQty)
          }}
          onFocus={event => event.target.select()}
          min={0}
          disabled={readOnly}
        />
      </Tooltip>
      {fromTracker ? (
        <FromWaste data-testid='stock-wastage-from-waste'>
          <span>{fromWasteLabel}</span>
          {dishLabel ? (
            <Dish data-testid='stock-wastage-dishes'>{dishLabel}</Dish>
          ) : null}
        </FromWaste>
      ) : null}
    </Cell>
  )
}

const norm = value =>
  String(value || '')
    .trim()
    .toLowerCase()

const catalogueSku = product => product?.sku || product?.product?.sku || ''

const catalogueTitle = product =>
  product?.title || product?.product?.title || ''

const catalogueSupplier = product =>
  product?.supplierTitle || product?.supplier?.title || product?.supplier || ''

/**
 * Catalogue product for this stock line.
 * A stored venue product id wins. Otherwise SKU and supplier, then title and
 * supplier when neither side has a SKU. Recipe lines and linked groups stay out.
 */
export const stockLineRecordProductId = (product, venueProducts = []) => {
  if (!product || product.recipeId || product.isLinkedStockGroup) return ''
  const stored = String(product.venueProductId || '').trim()
  if (stored) return stored
  const list = Array.isArray(venueProducts) ? venueProducts : []
  const lineSku = norm(product.sku)
  const lineSupplier = norm(product.supplierTitle)
  const lineTitle = norm(product.title)
  if (lineSku) {
    const bySku = list.find(
      item =>
        norm(catalogueSku(item)) === lineSku &&
        norm(catalogueSupplier(item)) === lineSupplier,
    )
    return bySku ? String(bySku._id || bySku.id || '') : ''
  }
  if (!lineTitle) return ''
  const byTitle = list.find(item => {
    if (norm(catalogueSku(item))) return false
    return (
      norm(catalogueTitle(item)) === lineTitle &&
      norm(catalogueSupplier(item)) === lineSupplier
    )
  })
  return byTitle ? String(byTitle._id || byTitle.id || '') : ''
}

/**
 * A product line with a wastage box and no catalogue match.
 * Recipe lines and linked groups skip Record waste for another reason.
 */
export const stockLineNotInCatalogue = (product, venueProducts = []) => {
  if (!product || product.recipeId || product.isLinkedStockGroup) return false
  const list = Array.isArray(venueProducts) ? venueProducts : []
  if (!list.length) return false
  return !stockLineRecordProductId(product, list)
}

/** Opens Record waste with this row’s product already selected. */
export const StockWastageRecordLink = ({
  venueId,
  lineId,
  venueProductId,
  stockTakeId,
  notInCatalogue = false,
}) => {
  if (!venueProductId) {
    if (!notInCatalogue) return null
    return (
      <MissingCatalogue
        data-testid={
          lineId
            ? `stock-wastage-not-in-catalogue-${lineId}`
            : 'stock-wastage-not-in-catalogue'
        }
      >
        {WASTAGE_COPY.notInCatalogue}
      </MissingCatalogue>
    )
  }
  return (
    <RecordLink
      to={wastageLinkTo(
        stockWastageRecordHref(venueId, venueProductId),
        wastageBackTarget(venueId, stockTakeId),
      )}
      data-testid={
        lineId ? `stock-record-waste-${lineId}` : 'stock-record-waste'
      }
    >
      {WASTAGE_COPY.record}
    </RecordLink>
  )
}

/** Column hint. Shown for every line, including ones nobody has logged yet. */
export const StockWastageColumnHint = ({ venueId, stockTake, stockTakeId }) => (
  <HintLink
    to={wastageLinkTo(
      stockWastagePeriodHref(venueId, stockTake),
      wastageBackTarget(venueId, stockTakeId || stockTake?._id),
    )}
    data-testid='stock-wastage-column-hint'
  >
    {WASTAGE_COPY.columnHint}
  </HintLink>
)

const Cell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const FromWaste = styled.span`
  display: inline-flex;
  flex-direction: column;
  color: #8c8c8c;
  font-size: 12px;
  line-height: 1.3;
  white-space: normal;
`

const Dish = styled.span`
  color: #595959;
`

const RecordLink = styled(Link)`
  display: block;
  margin-top: 4px;
  color: #8c8c8c;
  font-size: 12px;
  line-height: 1.3;
  white-space: nowrap;
  text-decoration: underline;
`

const MissingCatalogue = styled.span`
  display: block;
  margin-top: 4px;
  color: #8c8c8c;
  font-size: 12px;
  line-height: 1.3;
  white-space: normal;
`

const HintLink = styled(Link)`
  display: block;
  margin-top: 2px;
  color: #8c8c8c;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  white-space: normal;
  text-decoration: underline;
`
